
import { useParams, Link } from 'react-router-dom';
import React, { useEffect,useState, useRef } from 'react';
import axios from 'axios';
import { PDFDownloadLink, Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import ReactToPrint from 'react-to-print';
import config from '../config/config';
import { 
  Box, 
  Typography, 
  Paper, 
  Divider, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Card,
  Avatar,
  Chip,
  Badge,
  Grid,
  IconButton,
  Stack
} from '@mui/material';
import { 
  ReceiptLong, 
  FileDownload, 
  Print, 
  CheckCircle,
  AccessTime,
  ErrorOutline,
  School,
  ContactPhone,
  Email,
  CalendarToday
} from '@mui/icons-material';


// Mock Data for demonstration
const schoolInfo = {
  name: "Demo Academy",
  logo: "/logo.png",
  address: "Kenya, Nairobi",
  phone: "+254 750 347 547",
  email: "info@demoacademy.com",
  website: "www.demoacademy.edu"
};

const studentInfo = {
  id: "STU-2023-001",
  name: "John Smith",
  class: "Grade 8",
  admissionNumber: "ADM-2023-1234"
};

const receiptData = {
  id: "",
  invoiceNumber: "INV-2023-00156",
  issueDate: "2023-04-15",
  dueDate: "2023-05-15",
  status: "paid",
  paymentDate: "2023-04-20",
  paymentMethod: "Bank Transfer",
  transactionId: "TRX-789456123",
  items: [
    { id: 1, description: "Tuition Fee - Term 2", quantity: 1, unitPrice: 15000, total: 15000, paid: 15000, balance: 0 },
    { id: 2, description: "Library Fee", quantity: 1, unitPrice: 2000, total: 2000, paid: 2000, balance: 0 },
    { id: 3, description: "Computer Lab Fee", quantity: 1, unitPrice: 3000, total: 3000, paid: 3000, balance: 0 },
    { id: 4, description: "Sports Activities", quantity: 1, unitPrice: 1500, total: 1500, paid: 1500, balance: 0 }
  ]
};

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: 'KES',
    minimumFractionDigits: 2
  }).format(amount);
};

// Format date
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  
  const date = new Date(dateString);
  const options = { month: 'short', day: '2-digit', year: 'numeric' };
  return date.toLocaleDateString('en-US', options);
};





// Status component
const ReceiptStatus = ({ status }) => {

 
  const statusConfig = {
    paid: { color: 'success', icon: <CheckCircle className="mr-1" fontSize="small" />, label: 'Paid' },
    pending: { color: 'warning', icon: <AccessTime className="mr-1" fontSize="small" />, label: 'Pending' },
    overdue: { color: 'error', icon: <ErrorOutline className="mr-1" fontSize="small" />, label: 'Overdue' }
  };

  const config = statusConfig[status.toLowerCase()] || statusConfig.pending;

  
};

// PDF Document Definition
const ReceiptPDF = ({ schoolInfo, studentInfo, receiptData }) => (

  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        <View style={styles.logoContainer}>
          <Text style={styles.schoolName}>{schoolInfo.name}</Text>
        </View>
        <View style={styles.headerRight}>
          <Text style={styles.receiptTitle}>OFFICIAL RECEIPT</Text>
          <Text style={styles.receiptNumber}>{receiptData.id}</Text>
        </View>
      </View>
      
      <View style={styles.schoolInfo}>
        <Text>{schoolInfo.address}</Text>
        <Text>Phone: {schoolInfo.phone}</Text>
        <Text>Email: {schoolInfo.email}</Text>
        <Text>Website: {schoolInfo.website}</Text>
      </View>
      
      <View style={styles.divider} />
      
     
      
      <View style={styles.divider} />
      
      <View style={styles.tableContainer}>
        <View style={styles.tableHeader}>
          <Text style={[styles.tableHeaderCell, { flex: 2 }]}>DESCRIPTION</Text>
          <Text style={[styles.tableHeaderCell, { flex: 1, textAlign: 'center' }]}>QTY</Text>
          <Text style={[styles.tableHeaderCell, { flex: 1, textAlign: 'right' }]}>PRICE</Text>
          <Text style={[styles.tableHeaderCell, { flex: 1, textAlign: 'right' }]}>TOTAL</Text>
          <Text style={[styles.tableHeaderCell, { flex: 1, textAlign: 'right' }]}>PAID</Text>
          <Text style={[styles.tableHeaderCell, { flex: 1, textAlign: 'right' }]}>BALANCE</Text>
        </View>
        
        {receiptData.items.map((item, index) => (
          <View key={item.id} style={[styles.tableRow, { backgroundColor: index % 2 === 0 ? '#f9f9f9' : '#ffffff' }]}>
            <Text style={[styles.tableCell, { flex: 2 }]}>{item.description}</Text>
            <Text style={[styles.tableCell, { flex: 1, textAlign: 'center' }]}>{item.quantity}</Text>
            <Text style={[styles.tableCell, { flex: 1, textAlign: 'right' }]}>{formatCurrency(item.unitPrice).replace('KES', '')}</Text>
            <Text style={[styles.tableCell, { flex: 1, textAlign: 'right' }]}>{formatCurrency(item.total).replace('KES', '')}</Text>
            <Text style={[styles.tableCell, { flex: 1, textAlign: 'right' }]}>{formatCurrency(item.paid).replace('KES', '')}</Text>
            <Text style={[styles.tableCell, { flex: 1, textAlign: 'right' }]}>{formatCurrency(item.balance).replace('KES', '')}</Text>
          </View>
        ))}
      </View>
      
      <View style={styles.divider} />
      
      <View style={styles.summaryContainer}>
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>SUBTOTAL:</Text>
          <Text style={styles.summaryValue}>
            {formatCurrency(receiptData.items.reduce((sum, item) => sum + item.total, 0))}
          </Text>
        </View>
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>TOTAL PAID:</Text>
          <Text style={styles.summaryValue}>
            {formatCurrency(receiptData.items.reduce((sum, item) => sum + item.paid, 0))}
          </Text>
        </View>
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>BALANCE DUE:</Text>
          <Text style={styles.summaryValue}>
            {formatCurrency(receiptData.items.reduce((sum, item) => sum + item.balance, 0))}
          </Text>
        </View>
      </View>
      
      <View style={styles.divider} />
      
      <View style={styles.paymentInfo}>
        <Text style={styles.paymentInfoTitle}>PAYMENT INFORMATION</Text>
        <Text>Method: {receiptData.paymentMethod}</Text>
        <Text>Transaction ID: {receiptData.transactionId}</Text>
        <Text>Date: {formatDate(receiptData.paymentDate)}</Text>
      </View>
      
      <View style={styles.footer}>
        <Text style={styles.thankYouNote}>Thank you for your payment!</Text>
        <Text style={styles.footerText}>This is an automatically generated receipt and is valid without a signature.</Text>
        <Text style={styles.footerText}>For questions regarding this receipt, please contact the accounts office.</Text>
      </View>
    </Page>
  </Document>
);

// PDF Styles
const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontSize: 10,
    lineHeight: 1.5
  },
  header: {
    flexDirection: 'row',
    marginBottom: 20,
    justifyContent: 'space-between'
  },
  logoContainer: {
    width: '50%',
    flexDirection: 'column'
  },
  schoolName: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#3182CE'
  },
  headerRight: {
    width: '40%',
    alignItems: 'flex-end'
  },
  receiptTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#3182CE'
  },
  receiptNumber: {
    fontSize: 12,
    fontWeight: 'bold',
    color: '#3182CE'
  },
  schoolInfo: {
    marginBottom: 15
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
    marginVertical: 15
  },
  infoSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 15
  },
  infoColumn: {
    width: '45%'
  },
  infoTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 5,
    color: '#3182CE'
  },
  infoContent: {
    fontWeight: 'bold'
  },
  tableContainer: {
    marginVertical: 10
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#4A5568',
    paddingVertical: 8,
    paddingHorizontal: 5
  },
  tableHeaderCell: {
    color: 'white',
    fontWeight: 'bold',
    fontSize: 10
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 8,
    paddingHorizontal: 5,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0'
  },
  tableCell: {
    fontSize: 10
  },
  summaryContainer: {
    width: '50%',
    alignSelf: 'flex-end',
    marginVertical: 10
  },
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginVertical: 3
  },
  summaryLabel: {
    fontWeight: 'bold'
  },
  summaryValue: {
    fontWeight: 'bold'
  },
  paymentInfo: {
    marginVertical: 10
  },
  paymentInfoTitle: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 5,
    color: '#3182CE'
  },
  footer: {
    marginTop: 30,
    alignItems: 'center'
  },
  thankYouNote: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#3182CE',
    marginBottom: 10
  },
  footerText: {
    fontSize: 8,
    color: '#718096',
    textAlign: 'center'
  }
});

// Main component
export default function PremiumSchoolReceipt() {

  const componentRef = useRef();
  
  // Calculate totals
  const calculateTotals = () => {
    const total = receiptData.items.reduce((sum, item) => sum + item.total, 0);
    const paid = receiptData.items.reduce((sum, item) => sum + item.paid, 0);
    const balance = receiptData.items.reduce((sum, item) => sum + item.balance, 0);
    
    return { total, paid, balance };
  };
  
  const totals = calculateTotals();

   const [userDetails, setUserItems] = useState();
 const [latestInvoice, setInvoiceLatest] = useState(null);
const [getInvoiceItems, setInvoiceItems] = useState([]);
const { id, invoice_id } = useParams();
useEffect(() => {
  

  const token_ = localStorage.getItem("token");
 
  
  axios.get(config.API_URL+`/api/latestInvoiceV3/${id}`, {
    headers: {
      Authorization: `Bearer ${token_}`
    }
  })
  .then(response => {
   
    setInvoiceLatest({
      invoiceNumber: response.data.invoiceNumber,
      invoiceIssueDate: response.data.invoiceIssueDate,
      invoiceDueDate: response.data.invoiceDueDate,
      invoiceAmount: response.data.invoiceAmount,
      status: response.data.status,
      raw: response.data.invoice,
    });

    setInvoiceItems(response.data.invoice_items);
    setUserItems({
      name: response.data.name,
      totalBalance: response.data.totalBalance,
      totalPaidAmount: response.data.totalPaidAmount,
      transactionCode: response.data.transactionCode,
      paymentAmount: response.data.paymentAmount,
      paymentMethod: response.data.paymentMethod,
      paymentDate: response.data.paymentDate,
      admissionNumber: response.data.admissionNumber,
      class: response.data.class,
    });
     console.log(userDetails);
   
  })
  .catch(error => {
    console.error('Error fetching specific invoice info:', error);
  })
 
},[]);
  
  return (

    <Box className="bg-gray-50 min-h-screen p-4">
     {/* Actions Bar */}
      <Box 
        sx={{ 
          mt: 3, 
          display: 'flex', 
          justifyContent: 'center',
          gap: 2,
          flexWrap: 'wrap'
        }}
      >
        <ReactToPrint
          trigger={() => (
            <Button
              variant="contained"
              startIcon={<Print />}
              sx={{
                bgcolor: '#6B46C1',
                '&:hover': { bgcolor: '#553C9A' },
                borderRadius: '8px',
                px: 3,
                py: 1,
                fontWeight: 'medium',
                boxShadow: '0 4px 8px rgba(107, 70, 193, 0.2)',
                transition: 'all 0.3s ease'
              }}
            >
              Print Receipt
            </Button>
          )}
          content={() => componentRef.current}
        />
        
        <PDFDownloadLink
          document={<ReceiptPDF schoolInfo={schoolInfo} studentInfo={studentInfo} receiptData={receiptData} />}
          fileName={`Receipt_${receiptData.id}.pdf`}
          style={{ textDecoration: 'none' }}
        >
          {({ loading }) => (
            <Button
              variant="contained"
              startIcon={<FileDownload />}
              disabled={loading}
              sx={{
                bgcolor: '#3182CE',
                '&:hover': { bgcolor: '#2C5282' },
                borderRadius: '8px',
                px: 3,
                py: 1,
                fontWeight: 'medium',
                boxShadow: '0 4px 8px rgba(49, 130, 206, 0.2)',
                transition: 'all 0.3s ease'
              }}
            >
              {loading ? 'Preparing Download...' : 'Download PDF'}
            </Button>
          )}
        </PDFDownloadLink>
      </Box>

      <Paper 
        ref={componentRef}
        elevation={3} 
        className="max-w-4xl mx-auto shadow-lg rounded-xl overflow-hidden"
        sx={{
          backgroundColor: '#FFFFFF',
         
          borderRadius: '16px',
          marginTop:'20px',
          overflow: 'hidden'
        }}
      >
        {/* Receipt Header */}
        <Box 
          sx={{
            background: 'linear-gradient(135deg, #f0f5f5 0%, #f0f0f5 100%)',
            color: 'black',
            p: 4,
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ position: 'absolute', top: 0, right: 0, width: '150px', height: '150px', background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)', borderRadius: '50%' }}></Box>
          <Box sx={{ position: 'absolute', bottom: -50, left: -50, width: '250px', height: '250px', background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)', borderRadius: '50%' }}></Box>
          
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box display="flex" alignItems="center">
                <Avatar sx={{ bgcolor: 'white', color: '#3182CE', width: 60, height: 60, mr: 2 }}>
                  <School fontSize="large" />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight="bold" sx={{ letterSpacing: '0.5px' }}>
                    {schoolInfo.name}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
                    {schoolInfo.address}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box display="flex" flexDirection="column" alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
                <Box display="flex" alignItems="center" mb={1}>
                  
                    <Typography variant="h5" fontWeight="bold" letterSpacing={1}>
                      RECEIPT FOR {formatCurrency(userDetails?.paymentAmount)}
                    </Typography>
                 
                </Box>
                <Typography variant="h6" fontWeight="medium">
                  {receiptData.id}
                </Typography>
                <Box display="flex" alignItems="center" mt={1}>
                  <ContactPhone fontSize="small" sx={{ mr: 0.5, opacity: 0.8 }} />
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    {schoolInfo.phone}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" mt={0.5}>
                  <Email fontSize="small" sx={{ mr: 0.5, opacity: 0.8 }} />
                  <Typography variant="body2" sx={{ opacity: 0.8 }}>
                    {schoolInfo.email}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
        
        {/* Student and Receipt Information */}
        <Box sx={{ p: 4, bgcolor: '#F7FAFC' }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.03)' }}>
                <Box sx={{ p: 3, borderLeft: '4px solid #3182CE' }}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    BILLED TO
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {userDetails?.name}
                  </Typography>
                 
                  <Box display="flex" alignItems="center" mt={0.5}>
                    <Typography variant="body2" color="textSecondary" sx={{ mr: 1 }}>
                      Admission:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {userDetails?.admissionNumber}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" mt={0.5}>
                    <Typography variant="body2" color="textSecondary" sx={{ mr: 1 }}>
                      Class:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                     {userDetails?.class}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.03)' }}>
                <Box sx={{ p: 3, borderLeft: '4px solid #6B46C1' }}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    PAYMENT DETAILS
                  </Typography>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      Invoice #{latestInvoice?.invoiceNumber.split('-').pop()}
                    </Typography>
                    <ReceiptStatus status={receiptData.status} />
                  </Box>
                  <Box display="flex" alignItems="center" mt={1}>
                    <CalendarToday fontSize="small" sx={{ color: '#718096', mr: 1 }} />
                    <Typography variant="body2" color="textSecondary" sx={{ mr: 1 }}>
                      Issue Date:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatDate(latestInvoice?.invoiceIssueDate)}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" mt={0.5}>
                    <CalendarToday fontSize="small" sx={{ color: '#718096', mr: 1 }} />
                    <Typography variant="body2" color="textSecondary" sx={{ mr: 1 }}>
                      Due Date:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {formatDate(latestInvoice?.invoiceDueDate)}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" mt={0.5}>
                    <Typography variant="body2" color="textSecondary" sx={{ mr: 1 }}>
                      Transaction ID:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {userDetails?.transactionCode}
                    </Typography>
                  </Box>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Box>
        
        {/* Receipt Items Table */}
        <Box sx={{ p: 4 }}>
          <Typography variant="h6" fontWeight="bold" mb={3} sx={{ color: '#2D3748' }}>
            Receipt Items
          </Typography>
          <TableContainer sx={{ borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.03)' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#4A5568' }}>
                  <TableCell sx={{ fontWeight: 'bold', color: '#FFFFFF' }}>Description</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', color: '#FFFFFF' }}>Quantity</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', color: '#FFFFFF' }}>Unit Price</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', color: '#FFFFFF' }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', color: '#FFFFFF' }}>Paid</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold', color: '#FFFFFF' }}>Balance</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getInvoiceItems.map((item, index) => (
                  <TableRow
                    key={item.id}
                    sx={{
                      bgcolor: index % 2 === 0 ? '#F7FAFC' : '#FFFFFF',
                      '&:hover': { bgcolor: '#EBF4FF' },
                      transition: 'background-color 0.2s ease'
                    }}
                  >
                    <TableCell sx={{ borderLeft: '3px solid #3182CE', fontWeight: 'medium' }}>
                      {item.description}
                    </TableCell>
                    <TableCell align="center">{item.quantity}</TableCell>
                    <TableCell align="right">{formatCurrency(item.unitPrice)}</TableCell>
                    <TableCell align="right">{formatCurrency(item.total)}</TableCell>
                    <TableCell align="right" sx={{ color: '#38A169', fontWeight: 'medium' }}>
                      {formatCurrency(item.paid)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: item.balance > 0 ? '#E53E3E' : '#718096' }}>
                      {formatCurrency(item.balance)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Summary */}
          <Box display="flex" justifyContent="flex-end" mt={4}>
            <Card sx={{ width: { xs: '100%', md: '40%' }, borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
              <Box sx={{ p: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" mb={2} sx={{ color: '#2D3748' }}>
                  PAYMENT SUMMARY
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="textSecondary">
                    Subtotal:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatCurrency(userDetails?.paymentAmount)}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" color="textSecondary">
                    Total Paid:
                  </Typography>
                  <Typography variant="body1" fontWeight="medium" color="success.main">
                    {formatCurrency(userDetails?.totalPaidAmount)}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="subtitle1" fontWeight="bold">
                    Balance Due:
                  </Typography>
                  <Typography 
                    variant="subtitle1" 
                    fontWeight="bold"
                    color={totals.balance > 0 ? 'error.main' : 'text.secondary'}
                  >
                    {formatCurrency(userDetails?.totalBalance)}
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Box>
        </Box>
        
        {/* Note and Payment Method */}
        <Box sx={{ p: 4, bgcolor: '#F7FAFC' }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight="bold" mb={1} sx={{ color: '#2D3748' }}>
                PAYMENT METHOD
              </Typography>
              <Card sx={{ p: 2, borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                <Typography variant="body2">
                  {userDetails?.paymentMethod}
                </Typography>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight="bold" mb={1} sx={{ color: '#2D3748' }}>
                PAYMENT DATE
              </Typography>
              <Card sx={{ p: 2, borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                <Typography variant="body2">
                  {formatDate(userDetails?.paymentDate)}
                </Typography>
              </Card>
            </Grid>
          </Grid>
        </Box>
        
        {/* Footer */}
        <Box 
          sx={{ 
            p: 4, 
            bgcolor: '#fff', 
            color: 'black',
            textAlign: 'center'
          }}
        >
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
            Thank you for your payment!
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            This is an automatically generated receipt and is valid without a signature.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
            For questions regarding this receipt, please contact the accounts office.
          </Typography>
        </Box>
      </Paper>
      
     
    </Box>
  );
};